import {
  ButtonIcon,
  Caption,
  css,
  DuplicateIcon,
  EllipsisText,
  EnterIcon,
  ListItem,
  Paragraph,
  PlusIcon,
  styled,
  TickIcon,
  Tooltip,
} from 'canoe';
import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { ValueBox, ValueSelectInput } from '@candu-ai/craft/ui';
import { NewListItem } from 'canoe/components/ListItem';

interface Props {
  value?: string;
  onChange: (s?: string) => void;
}

type Item = { label: string; value: string; searching?: boolean; disabled?: boolean };

type EventQueryData = { eventName: string; trackingEventType?: string };

const EnterContainer = styled(Paragraph)`
  display: flex;
  font-size: 12px;
`;

const LabelCopy = styled.div`
  ${({ theme }) => css`
    border-right: 1px solid ${theme.colors.GlitterGrayD10};
    margin: -6px ${theme.spaces.xxs} -6px -4px;
    padding-right: ${theme.spaces.xxs};
    display: flex;
    align-items: center;
  `}
`;

const renderItem = (itemProps: any) => {
  if (itemProps.searching) {
    return (
      <NewListItem selected>
        {itemProps.label}
        <EnterContainer mb="none">
          Press Enter to save <EnterIcon ml="xxs" color="OnyxGrayL30" />
        </EnterContainer>
      </NewListItem>
    );
  }

  if (!itemProps.value) {
    return (
      <NewListItem>
        <EnterContainer color="OnyxGrayL30" mb="none">
          <PlusIcon /> {itemProps.label}
        </EnterContainer>
      </NewListItem>
    );
  }

  return <ListItem {...itemProps}>{itemProps.label}</ListItem>;
};

const getFormattedResults = (data: EventQueryData[]) => {
  return data.map((item) => ({
    label: item.eventName,
    value: item.eventName,
  }));
};

export const InteractionLabelDropdown = ({ value, onChange }: Props) => {
  const { getAll } = useCreatePayload(ENDPOINTS.autoComplete);
  const [copied, setCopied] = useState(false);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isTypingSearch, setIsTypingSearch] = useState(false);
  const payload = getAll({
    query: {
      finder: 'byInteractionLabel',
      q: `${searchValue.trim()}`,
    },
  });
  const { data: results, isFetching } = useQuery(createCacheKey(payload), () =>
    fetchResource<EventQueryData[]>(payload),
  );

  const sectionOptions: Item[] = useMemo(() => {
    if (!results) {
      return value ? [{ label: value, value, searching: false }] : [];
    }
    const alreadyExists =
      !isEmpty(searchValue) &&
      results.find((item) => item.eventName.toLowerCase() === searchValue.toLowerCase());

    const formattedResults = getFormattedResults(results);

    if (alreadyExists) {
      return formattedResults.filter((item) =>
        item.label.toLowerCase().includes(searchValue?.toLowerCase()),
      );
    }

    const isValueInList = results.find((item) => item.eventName === value);

    return [
      ...[
        {
          value: '',
          label: searchValue || 'Start typing to create a new Label',
          searching: Boolean(searchValue),
          disabled: isTypingSearch || !searchValue,
        },
      ],
      ...(value && !isValueInList ? [{ label: value, value }] : []),
      ...formattedResults.filter((item) =>
        item.label.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    ];
  }, [value, results, isFetching, searchValue, isTypingSearch, getFormattedResults]);

  const onClearSearch = useCallback(() => {
    setSearchValue('');
    setIsTypingSearch(false);
  }, []);

  const debouncedSetSearchValue = useCallback(
    debounce((v) => {
      setSearchValue(v);
      setIsTypingSearch(false);
    }, 300),
    [],
  );

  const updateSearchValue = (e: ChangeEvent<HTMLInputElement>) => {
    setIsTypingSearch(true);
    debouncedSetSearchValue(e.target.value);
  };

  const selectedItem = useMemo(() => sectionOptions.find((option) => option.value === value), [
    sectionOptions,
    value,
  ]);

  const preventKeyboardSelect = useMemo(() => {
    if (!results) {
      return true;
    }

    const searchAlreadyExists =
      !isEmpty(searchValue) &&
      !!results.find((item) => item.eventName.toLowerCase() === searchValue.toLowerCase());

    return !searchAlreadyExists && Boolean(searchValue) && isFetching;
  }, [results, searchValue, isFetching]);

  const handleCopy = useCallback(
    (event: MouseEvent, copyValue: string) => {
      event.stopPropagation();

      if (!copied) {
        setCopied(true);

        navigator.clipboard.writeText(`${copyValue}`).then(() => {
          // change button state back to normal after copy
          setTimeout(() => {
            copyButtonRef.current?.blur();
            setCopied(false);
          }, 2000);
        });
      }
    },
    [copied, copyButtonRef.current],
  );

  return (
    <ValueBox>
      <ValueSelectInput<Item>
        scale="small"
        searchable
        clearable={false}
        items={sectionOptions}
        loading={isFetching}
        renderItem={renderItem}
        calloutOffset="xxs"
        renderValue={(valueProps) => (
          <div css={{ display: 'flex' }}>
            <LabelCopy>
              <Tooltip
                placement="left"
                offset="xs"
                content={
                  <Caption color="White" mb="none">
                    {copied ? 'Copied!' : 'Copy'}
                  </Caption>
                }
              >
                <ButtonIcon
                  ref={copyButtonRef}
                  size="xsmall"
                  icon={copied ? TickIcon : DuplicateIcon}
                  onClick={(e: MouseEvent) => handleCopy(e, valueProps.selectedItem.value)}
                  radius="small"
                  color={copied ? 'secondary' : 'contrast'}
                />
              </Tooltip>
            </LabelCopy>
            <EllipsisText
              content={
                <Caption color="White" mb="none">
                  {valueProps.selectedItem.value}
                </Caption>
              }
              offset="s"
              placement="top"
            >
              <span>{valueProps.selectedItem.value}</span>
            </EllipsisText>
          </div>
        )}
        onFilter={updateSearchValue}
        onClearFilter={onClearSearch}
        onOpeningChange={onClearSearch}
        selectedItem={selectedItem}
        onSelect={(nv) => !nv?.disabled && onChange(nv?.label)}
        searchInputProps={{
          preventKeyboardSelect: isTypingSearch || preventKeyboardSelect,
        }}
      />
    </ValueBox>
  );
};
